import { useEffect, useRef } from 'react';

/**
 * useBackClose — make the phone's Back button close an overlay (lightbox,
 * chat sheet, drawer) instead of leaving the page.
 *
 * The browser only knows about history entries, so an overlay that opens
 * without pushing one turns Back into "eject me from the page" — on a phone
 * that's a reflex press, and it was dumping the user from a photo all the
 * way back to the estimates list (dogfood 2026-09-24).
 *
 * While `open` is true this pushes ONE same-URL history entry tagged as an
 * overlay sentinel. Back consumes the sentinel and we close the overlay,
 * staying on the page. Closing via the overlay's own UI (X, Esc) removes the
 * sentinel with a silent history.back() so a later Back press isn't dead.
 *
 * Coordination, because several parties listen to popstate:
 *  - A module-level stack tracks open overlays; the ONE dispatcher listener
 *    (registered at import time, so it runs before any hook-registered
 *    listener) closes the top overlay and tags the event via
 *    `overlayHandledPopstate`. useUnsavedGuard checks that tag so a Back
 *    press meant for a photo never triggers the "discard changes?" dialog.
 *  - Overlays stack (photo on top of chat): each Back closes the top one.
 *
 * Same-URL entries keep React Router's location unchanged — same trick
 * useUnsavedGuard already uses, so the two compose predictably.
 */

const HANDLED = '__koOverlayHandledPopstate';
const stack = []; // [{ token, close }] — top of stack is last element.
let swallowNext = false; // silent back() from a UI close — eat the popstate.

/** True when the overlay dispatcher already consumed this popstate event. */
export function overlayHandledPopstate(e) {
  return !!(e && e[HANDLED]);
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (e) => {
    if (swallowNext) {
      swallowNext = false;
      e[HANDLED] = true;
      return;
    }
    const top = stack.pop();
    if (top) {
      e[HANDLED] = true;
      top.closedByPop = true;
      top.close();
    }
  });
}

export function useBackClose(open, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === 'undefined' || !window.history) return undefined;

    const entry = {
      token: `ko-overlay-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      closedByPop: false,
      close: () => closeRef.current?.(),
    };
    window.history.pushState({ koOverlay: entry.token }, '');
    stack.push(entry);

    return () => {
      if (entry.closedByPop) return; // Back already consumed the sentinel.
      const i = stack.indexOf(entry);
      if (i >= 0) stack.splice(i, 1);
      // Closed via the overlay's own UI: remove our sentinel only if it is
      // still the live entry — if navigation happened on top of the overlay,
      // backing up would undo that navigation instead.
      if (window.history.state?.koOverlay === entry.token) {
        swallowNext = true;
        window.history.back();
      }
    };
  }, [open]);
}

export default useBackClose;
